"use client";

import React, { useState } from "react";
import wander from "@/assest/home2.gif";
import Image from "next/image";
import Title from "@/core/common/Title";
import { IoStar } from "react-icons/io5";
import { ITestimonialsRecord } from "./interface/ITestimonials";

interface WhyLoveUsProps {
  testimonialsData?: ITestimonialsRecord[];
}

const WhyLoveUs = ({ testimonialsData }: WhyLoveUsProps) => {
  const testimonials = testimonialsData ?? [];
  const [index, setIndex] = useState(() =>
    Math.min(2, Math.max(0, testimonials.length - 1))
  );

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, "");

  return (
    <div className="mt-60 relative">
      <Title greenText="Why Travellers " yellowText="Love Us" />

      <div className="mt-24 md:mt-64 relative w-full overflow-visible">
        <div
          className="flex transition-transform ease-in-out duration-1000 scale-100 items-start h-150"
          style={{
            transform: `translateX(calc(50% - ${index * 254}px - 200px))`,
          }}
        >
          {testimonials.map((item: ITestimonialsRecord, i: number) => {
            const isActive = i === index;
            return (
              <div
                key={i}
                className={`mr-6 relative overflow-hidden transform transition-all items-center justify-center duration-500 ${
                  isActive
                    ? "min-w-92 z-10 scale-105 -translate-y-10 md:-translate-y-52"
                    : "min-w-56 z-10 scale-100"
                }`}
              >
                <div
                  className={`relative overflow-hidden rounded-full transition-all duration-500 flex items-center justify-center ${
                    isActive
                      ? "h-36 w-36 md:h-92 md:w-92 mx-auto"
                      : "h-36 w-36 md:h-60 md:w-56"
                  }`}
                >
                  <Image
                    src={item?.image || "placeholder.png"}
                    alt={`Image ${i + 1}`}
                    fill
                    className="object-cover"
                  />
                </div>

                {isActive && (
                  <div className="ml-0 bg-white text-center justify-center items-center typography-extra-large-body-light text-grey-500 mt-14">
                    <div className="flex items-center gap-10 whitespace-nowrap">
                      <div className="shrink-0 bg-primary-500 text-white px-4 py-1 rounded-2xl">
                        <span className="inline-flex items-center gap-2 font-medium text-sm">
                          <IoStar className="text-yellow-400" /> {item?.rating}
                        </span>
                      </div>
                      <h3 className="typography-h6-regular text-black m-0">
                        {item?.name}
                      </h3>
                    </div>
                    <p
                      className="text-sm text-grey-500 line-clamp-4 w-72 mx-auto mt-4"
                      title={stripHtml(item.message)}
                      dangerouslySetInnerHTML={{
                        __html: item?.message || "",
                      }}
                    ></p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="pointer-events-none absolute inset-y-0 -left-20 w-24 z-30 bg-linear-to-r from-white/80 to-transparent blur-sm" />
        <div className="pointer-events-none absolute inset-y-0 -right-20 w-24 z-30 bg-linear-to-l from-white/80 to-transparent blur-sm" />

        <button
          onClick={prevSlide}
          className="bg-[#F0F0F0] text-black text-lg font-bold px-3 py-2 rounded-full absolute left-[33%] bottom-[34%] sm:bottom-[40%] md:bottom-[30%] lg:bottom-[45%] flex gap-3 z-10"
        >
          ←
        </button>

        <button
          onClick={nextSlide}
          className="bg-[#F0F0F0] text-black text-lg font-bold px-3 py-2 rounded-full absolute right-[33%] bottom-[34%] sm:bottom-[40%] md:bottom-[30%] lg:bottom-[45%] flex gap-3 z-10"
        >
          →
        </button>
      </div>
      <div className="hidden md:block shrink-0 absolute right-0 bottom-0">
        <Image
          src={wander}
          alt="holiday"
          className="hidden md:block h-44 w-44"
        />
      </div>
    </div>
  );
};

export default WhyLoveUs;
